import { ColumnData, PaginatedDoctorItem } from '../interface/interface';
import { Table } from '../components/Table';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { getPaginatedDoctors } from '../service';
import { PaginationState } from '@tanstack/react-table';
import Loading from '../components/Loading';
import { Link, useNavigate } from 'react-router-dom';
import { OrderSummaryIcon } from '../assets/svgImages';
import showToast from '../components/ToastComponent';
import { DoctorFilters } from './DoctorFilters';
import { SingleValue } from 'react-select';

export const DoctorTab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [doctorData, setDoctorData] = useState<Array<PaginatedDoctorItem>>([]);
  const [filter, setFilter] = useState<
    SingleValue<{
      label: string;
      value: string;
    }>
  >({ label: 'All', value: 'all' });

  const [activeFilter, setActiveFilter] = useState<
    SingleValue<{
      label: string;
      value: string;
    }>
  >({ label: 'Active', value: 'active' });
  const columns: Array<ColumnData> = [
    {
      key: 'firstName',
      header: 'First Name',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'lastName',
      header: 'Last Name',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'email',
      header: 'Email',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'prescriberNumber',
      header: 'Prescriber Number',
      cell: (info) => info.getValue(),
    },
    {
      key: 'ahpraNumber',
      header: 'Ahpra Number',
      cell: (info) => info.getValue(),
    },
    {
      key: 'isActive',
      header: 'Active',
      cell: (info) => (info.getValue() ? 'Active' : 'Inactive'),
    },
    {
      key: 'internal',
      header: 'Internal',
      cell: (info) => (info.getValue() ? 'Internal' : 'External'),
    },
    {
      key: 'edit',
      header: () => 'Edit',
      cell: (info) => (
        <button
          type="button"
          className=""
          onClick={() => navigate(`/doctor/edit/${info.row.original.id}`)}
        >
          <OrderSummaryIcon />
        </button>
      ),
    },
  ];

  useEffect(() => {
    if (!user?.access_token) return;
    setLoading(true);

    getPaginatedDoctors(
      user.access_token,
      pagination.pageIndex,
      pagination.pageSize,
      '',
      filter?.value,
      activeFilter?.value,
    ).then((data): void => {
      if (typeof data === 'number') {
        showToast({
          message: 'Unable to fetch Doctors!',
          type: 'info',
        });
        setLoading(false);
        return;
      }

      setTotalRecords(data.total);
      setDoctorData(data.data);
      setLoading(false);
    });
  }, [pagination, filter, activeFilter]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <div className='items-center mb-5"'>
        <h1 className="text-xl font-semibold leading-6 text-primary-blue-500">
          Doctors
        </h1>
      </div>
      <div className="flex justify-between items-center my-5">
        <DoctorFilters
          filter={filter}
          setFilter={setFilter}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
        />
        <div className="flex justify-end items-center gap-2">
          <Link to={'/upload/training-tools'}>
            <button
              type="button"
              className="ml-2 block rounded-md bg-secondary-cornflowerblue-600 px-10 py-3 text-center text-sm font-semibold text-white flex items-center"
            >
              View & Upload Resources
            </button>
          </Link>
          <Link to={'/doctor/edit'}>
            <button
              type="button"
              className="ml-2 block rounded-md bg-secondary-cornflowerblue-600 px-10 py-3 text-center text-sm font-semibold text-white flex items-center"
            >
              🩺
              <span className="ml-3">Add New Doctor</span>
            </button>
          </Link>
        </div>
      </div>
      <Table
        columns={columns}
        data={doctorData}
        enablePagination
        enableSorting
        pagintation={{
          state: pagination,
          setter: setPagination,
          totalRecords: totalRecords,
        }}
      />
    </>
  );
};
